import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, PasswordInput, Select, SelectItem, TextInput, OverflowMenu, OverflowMenuItem, Checkbox, Tabs, TabList, Tab, TabPanels, TabPanel } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { PageHeader, InlineError, ErrorState, BlockSkeleton } from '../components/states';
import { When } from '../components/format';
import { StatusTag } from '../components/labels';

type Admin = { id: string; username: string; displayName: string; status: string; role: string; roleLabel: string; createdAt: string; lastLoginAt: string | null; lockedUntil: string | null; telegramUserId: string | null };
type Roles = { roles: { name: string; label: string; permissions: string[]; users: number }[]; permissions: Record<string, string> };
type Secret = { temporaryPassword?: string };

function CreateAdmin({ roles }: { roles: Roles['roles'] }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ username: '', displayName: '', role: 'operator', password: '' });
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post('/api/admin/admins', f),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admins'] }); setOpen(false); setF({ username: '', displayName: '', role: 'operator', password: '' }); },
  });
  return (
    <>
      <Button renderIcon={Add} onClick={() => setOpen(true)}>Thêm quản trị viên</Button>
      <Modal open={open} modalHeading="Thêm quản trị viên" primaryButtonText="Tạo tài khoản" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending || f.password.length < 12 || !f.username || !f.displayName}
        onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <TextInput id="nu" labelText="Tên đăng nhập" helperText="a-z, 0-9, dấu chấm, gạch dưới, gạch ngang" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} />
          <TextInput id="nd" labelText="Tên hiển thị" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} />
          <Select id="nr" labelText="Vai trò" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            {roles.map((r) => <SelectItem key={r.name} value={r.name} text={r.label} />)}
          </Select>
          <PasswordInput showPasswordLabel="Hiện mật khẩu" hidePasswordLabel="Ẩn mật khẩu" id="np" labelText="Mật khẩu ban đầu" helperText="Tối thiểu 12 ký tự. Người dùng tự đổi sau lần đăng nhập đầu." value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <InlineError error={m.error} />
        </div>
      </Modal>
    </>
  );
}

function RolesEditor({ data }: { data: Roles }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, string[]>>({});
  const save = useMutation({
    mutationFn: (name: string) => api.put(`/api/admin/roles/${name}`, { permissions: edit[name] }),
    onSuccess: (_r, name) => { qc.invalidateQueries({ queryKey: ['roles'] }); setEdit((e) => { const n = { ...e }; delete n[name]; return n; }); },
  });
  // quyen cua trang nhat ky / lich su cau hinh da bo (khach 2026-10-02): khong hien trong bang quyen
  const perms = Object.entries(data.permissions).filter(([k]) => !HIDDEN_PERMS.has(k));
  return (
    <div className="stack">
      {data.roles.map((r) => {
        const cur = edit[r.name] ?? r.permissions;
        const locked = r.name === 'founder' || me?.role !== 'founder';
        return (
          <section key={r.name} className="section">
            <h2>{r.label} <span className="muted">({r.users} người)</span></h2>
            <fieldset disabled={locked} className="perm-grid">
              {perms.map(([p, label]) => (
                <Checkbox key={p} id={`${r.name}-${p}`} labelText={label} checked={cur.includes(p)} disabled={locked}
                  onChange={(_e, { checked }) => setEdit({ ...edit, [r.name]: checked ? [...cur, p] : cur.filter((x) => x !== p) })} />
              ))}
            </fieldset>
            {edit[r.name] ? <div className="row" style={{ marginTop: 12 }}>
              <Button size="sm" onClick={() => save.mutate(r.name)} disabled={save.isPending}>Lưu quyền {r.label}</Button>
              <Button size="sm" kind="ghost" onClick={() => setEdit((e) => { const n = { ...e }; delete n[r.name]; return n; })}>Bỏ thay đổi</Button>
            </div> : null}
          </section>
        );
      })}
      <InlineError error={save.error} />
      {me?.role !== 'founder' ? <p className="muted">Chỉ chủ sở hữu sửa được quyền của vai trò.</p> : null}
    </div>
  );
}

const HIDDEN_PERMS = new Set(['audit.view', 'config.history']);

export function AdminsPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const admins = useQuery({ queryKey: ['admins'], queryFn: () => api.get<Admin[]>('/api/admin/admins') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Roles>('/api/admin/roles') });
  const [secret, setSecret] = useState<Secret | null>(null);
  // Telegram user id that may press Duyet / Tu choi in the nap group as this admin
  const [tg, setTg] = useState<{ admin: Admin; id: string } | null>(null);
  const saveTg = useMutation({
    mutationFn: () => api.patch(`/api/admin/admins/${tg!.admin.id}`, { telegramUserId: tg!.id.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admins'] }); setTg(null); },
  });
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, string> }) => api.patch(`/api/admin/admins/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admins'] }),
  });
  const reset = useMutation({
    mutationFn: (id: string) => api.post<Secret>(`/api/admin/admins/${id}/reset`),
    onSuccess: (r) => setSecret(r),
  });

  const columns: ColumnDef<Admin, any>[] = [
    { header: 'Tên đăng nhập', accessorKey: 'username', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Tên hiển thị', accessorKey: 'displayName' },
    { header: 'Vai trò', accessorKey: 'roleLabel' },
    { header: 'Trạng thái', accessorKey: 'status', cell: (c) => (c.getValue() === 'ACTIVE' ? <StatusTag label="Hoạt động" type="green" /> : <StatusTag label="Đã khóa" type="gray" />) },
    { header: 'Telegram duyệt nạp', id: 'tg', enableSorting: false, cell: (c) => {
      const a = c.row.original;
      return <span className="row">{a.telegramUserId ? <span className="num">{a.telegramUserId}</span> : <span className="muted">—</span>}
        <Button kind="ghost" size="sm" onClick={() => { saveTg.reset(); setTg({ admin: a, id: a.telegramUserId ?? '' }); }}>{a.telegramUserId ? 'Đổi' : 'Liên kết'}</Button></span>;
    } },
    { header: 'Tạo lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
    {
      header: '', id: 'actions', enableSorting: false,
      cell: (c) => {
        const a = c.row.original;
        if (a.id === me?.id) return <span className="muted">Bạn</span>;
        return (
          <OverflowMenu size="sm" flipped aria-label={`Thao tác cho ${a.username}`}>
            {a.status === 'ACTIVE'
              ? <OverflowMenuItem itemText="Khóa tài khoản" isDelete onClick={() => patch.mutate({ id: a.id, body: { status: 'DISABLED' } })} />
              : <OverflowMenuItem itemText="Mở khóa" onClick={() => patch.mutate({ id: a.id, body: { status: 'ACTIVE' } })} />}
            <OverflowMenuItem itemText="Cấp lại mật khẩu" onClick={() => reset.mutate(a.id)} />
            {(roles.data?.roles ?? []).filter((r) => r.name !== a.role).map((r) => (
              <OverflowMenuItem key={r.name} itemText={`Đổi vai trò: ${r.label}`} onClick={() => patch.mutate({ id: a.id, body: { role: r.name } })} />
            ))}
          </OverflowMenu>
        );
      },
    },
  ];

  return (
    <div className="page">
      <PageHeader title="Quản trị viên" lead="Tài khoản CMS, vai trò và quyền. Đăng nhập bằng mật khẩu. Sai 8 lần thì khóa 15 phút."
        actions={roles.data ? <CreateAdmin roles={roles.data.roles} /> : null} />
      <InlineError error={patch.error ?? reset.error} />
      <Tabs>
        <TabList aria-label="Quản trị viên và vai trò">
          <Tab>Tài khoản</Tab>
          <Tab>Vai trò và quyền</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <DataTable columns={columns} data={admins.data} loading={admins.isLoading} error={admins.error} onRetry={() => admins.refetch()} rowKey={(r) => r.id}
              empty={{ title: 'Chưa có quản trị viên' }} />
          </TabPanel>
          <TabPanel>
            {roles.isLoading ? <BlockSkeleton /> : roles.error ? <ErrorState error={roles.error} /> : roles.data ? <RolesEditor data={roles.data} /> : null}
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Modal open={!!tg} modalHeading={'Telegram duyệt nạp · ' + (tg?.admin.username ?? '')} primaryButtonText="Lưu" secondaryButtonText="Hủy"
        primaryButtonDisabled={saveTg.isPending || !/^\d{0,19}$/.test(tg?.id.trim() ?? '')}
        onRequestClose={() => setTg(null)} onRequestSubmit={() => saveTg.mutate()}>
        <div className="stack">
          <p>Telegram này bấm được nút Duyệt / Từ chối trên tin lệnh nạp trong nhóm nạp, với quyền của tài khoản này (cần quyền duyệt nạp). Để trống là bỏ liên kết.</p>
          <TextInput id="tg-id" labelText="Telegram ID (dãy số)" helperText="Bấm thử một nút trong nhóm nạp: bot báo Telegram ID của bạn." value={tg?.id ?? ''}
            onChange={(e) => setTg((t) => (t ? { ...t, id: e.target.value.replace(/\D/g, '') } : t))} />
          <InlineError error={saveTg.error} />
        </div>
      </Modal>
      <Modal open={!!secret} passiveModal modalHeading="Mật khẩu tạm" onRequestClose={() => setSecret(null)}>
        {secret?.temporaryPassword ? <p>Mật khẩu mới: <span className="num">{secret.temporaryPassword}</span>. Đưa cho người đó một lần, rồi họ tự đổi.</p> : null}
      </Modal>
    </div>
  );
}
